import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Container,
} from '@mui/material';
import { Visibility, VisibilityOff, PersonAdd } from '@mui/icons-material';
import { authService } from '../services/authService';
import { toast } from 'react-toastify';
import {
  authPageStyles,
  authInputStyles,
  authButtonStyles,
  authIconButtonStyles,
  authLinkStyles,
  authLinkTextStyles,
  welcomeSectionStyles,
  welcomeContentStyles,
  welcomeTitleStyles,
  welcomeDescriptionStyles,
  decorativeCircle1Styles,
  decorativeCircle2Styles,

} from '../../../assets/styles';

// Validation schema
const registerSchema = Yup.object({
  username: Yup.string()
    .min(3, 'Username phải có ít nhất 3 ký tự')
    .required('Username là bắt buộc'),
  email: Yup.string()
    .email('Email không hợp lệ')
    .required('Email là bắt buộc'),
  password: Yup.string()
    .min(6, 'Mật khẩu phải có ít nhất 6 ký tự')
    .required('Mật khẩu là bắt buộc'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Mật khẩu xác nhận không khớp')
    .required('Xác nhận mật khẩu là bắt buộc'),
});

const RegisterPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');

  const formik = useFormik({
    initialValues: {
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: registerSchema,
    onSubmit: async (values) => {
      setError('');
      try {
        await authService.register(values);
        toast.success('Đăng ký thành công! Vui lòng kiểm tra email để xác thực tài khoản.');
        navigate('/login');
      } catch (err) {
        const msg = err.response?.data?.message || 'Đăng ký thất bại';
        setError(msg);
        toast.error(msg);
      }
    },
  });

  return (
    <Box sx={authPageStyles.container}>
      <Container maxWidth="md">
        <Box sx={authPageStyles.mainBox}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'stretch',
              gap: 3,
            }}
          >
            {/* FORM SECTION */}
            <Box sx={[authPageStyles.formSection, { flex: 1 }]}>
              <Box sx={authPageStyles.headerSection}>
                <Typography variant="h4" sx={authPageStyles.logoText}>
                  GPT Team
                </Typography>
                <Typography variant="h6" sx={authPageStyles.pageTitle}>
                  Đăng ký tài khoản
                </Typography>
                <Typography variant="body2" sx={authPageStyles.subtitle}>
                  Tạo tài khoản mới để bắt đầu
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={authPageStyles.alert}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={formik.handleSubmit}>
                {/* USERNAME */}
                <TextField
                  fullWidth
                  id="username"
                  name="username"
                  label="Tên đăng nhập"
                  placeholder="Nhập tên đăng nhập"
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  error={formik.touched.username && Boolean(formik.errors.username)}
                  helperText={formik.touched.username && formik.errors.username}
                  sx={authInputStyles}
                />

                {/* EMAIL */}
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email"
                  placeholder="example@mail.com"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  sx={authInputStyles}
                />

                {/* PASSWORD */}
                <TextField
                  fullWidth
                  id="password"
                  name="password"
                  label="Mật khẩu"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={formik.touched.password && formik.errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={authIconButtonStyles}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={authInputStyles}
                />

                {/* CONFIRM PASSWORD */}
                <TextField
                  fullWidth
                  id="confirmPassword"
                  name="confirmPassword"
                  label="Xác nhận mật khẩu"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)
                  }
                  helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          sx={authIconButtonStyles}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ ...authInputStyles, mb: 3 }}
                />

                {/* SUBMIT BUTTON */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={formik.isSubmitting}
                  startIcon={formik.isSubmitting ? <CircularProgress size={20} /> : <PersonAdd />}
                  sx={authButtonStyles}
                >
                  {formik.isSubmitting ? 'Đang đăng ký...' : 'Đăng ký'}
                </Button>

                {/* LOGIN LINK */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={authLinkTextStyles}>
                    Đã có tài khoản?{' '}
                    <Link to="/login" style={authLinkStyles}>
                      Đăng nhập ngay
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* WELCOME SECTION */}
            <Box sx={[welcomeSectionStyles, { flex: 1 }]}>
              <Box sx={welcomeContentStyles}>
                <Typography variant="h4" sx={welcomeTitleStyles}>
                  Bắt đầu
                  <br />
                  hành trình!
                </Typography>
                <Typography variant="body1" sx={welcomeDescriptionStyles}>
                  Tham gia cùng chúng tôi để trải nghiệm hệ thống quản lý GPT Team hiệu quả.
                  Đăng ký ngay để khám phá những tính năng tuyệt vời.
                </Typography>
              </Box>

              {/* Decorative circles */}
              <Box sx={decorativeCircle1Styles} />
              <Box sx={decorativeCircle2Styles} />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default RegisterPage;
